/**
 * Office.js Context Provider Component
 * Initializes Office.js and provides state to the app
 */
import { type ReactNode, useEffect, useState } from 'react';

import { initializeOffice } from '@/config/office/office';
import { OfficeContextState } from '@/config/office/types';
import { useOfficeContextStore } from '@/utils/stores/useOfficeContextStore';

import { OfficeContext } from './OfficeContext';

interface OfficeProviderProps {
  children: ReactNode;
}

export function OfficeProvider({ children }: OfficeProviderProps) {
  const initializeContextStore = useOfficeContextStore((state) => state.initialize);

  const [state, setState] = useState<OfficeContextState>({
    error: null,
    host: null,
    isInitialized: false,
    isLoading: true,
    platform: null,
  });

  useEffect(() => {
    // Initialize Office.js
    initializeOffice()
      .then((info) => {
        setState({
          error: null,
          host: info.host,
          isInitialized: true,
          isLoading: false,
          platform: info.platform,
        });

        // Initialize Office context store once Office.js is ready
        initializeContextStore();
      })
      .catch((error) => {
        console.error('Office.js initialization failed:', error);
        setState({
          error: error instanceof Error ? error : new Error(String(error)),
          host: null,
          isInitialized: false,
          isLoading: false,
          platform: null,
        });
      });
  }, [initializeContextStore]);

  return <OfficeContext.Provider value={state}>{children}</OfficeContext.Provider>;
}
